import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Building2, Users } from "lucide-react";
import { toast } from "sonner";

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", code: "" });
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: "", code: "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, code: c.code }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/companies/${editing.id}`, form);
      else await api.post("/companies", form);
      toast.success(editing ? "Empresa actualizada" : "Empresa creada");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/companies/${delTarget.id}`);
      toast.success("Empresa eliminada");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 sm:p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Empresas</h1>
          <p className="text-muted-foreground text-sm mt-1">Administra las empresas y sus administradores.</p>
        </div>
        <Button className="rounded-sm gap-2 bg-[#DC2626] hover:bg-[#DC2626]/90" onClick={openNew} data-testid="add-company-button">
          <Plus className="h-4 w-4" /> Nueva empresa
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {companies.map((c) => (
          <div key={c.id} className="bg-card border border-border rounded-sm p-5" data-testid={`company-card-${c.code}`}>
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 bg-muted rounded-sm flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[#DC2626]" />
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" onClick={() => openEdit(c)} data-testid={`edit-company-${c.code}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-[#E11D48]" onClick={() => setDelTarget(c)} data-testid={`delete-company-${c.code}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <h3 className="font-heading font-semibold text-lg mt-4">{c.name}</h3>
            <div className="label-caps mt-1">Código: {c.code}</div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground mt-4 pt-4 border-t border-border">
              <Users className="h-4 w-4" /> {c.users_count} usuarios · {c.admins_count} admins
            </div>
          </div>
        ))}
        {!companies.length && (
          <div className="col-span-full text-center text-muted-foreground py-16 border border-dashed border-border rounded-sm">
            No hay empresas registradas todavía.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Editar empresa" : "Nueva empresa"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="label-caps">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="company-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="label-caps">Código</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-sm" data-testid="company-code-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="rounded-sm bg-[#DC2626] hover:bg-[#DC2626]/90" onClick={save} data-testid="save-company-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará "{delTarget?.name}" junto con sus usuarios y datos de servicios. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-sm bg-[#E11D48] hover:bg-[#E11D48]/90" onClick={remove} data-testid="confirm-delete-company">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
